import type {
  SubmitResponse,
  SubmitStatus,
} from '../../graphql/mutation/submit';
import { useState, useEffect } from 'react';
import { Layout } from '../Layout/Layout';
import { queryClient } from '../../queryClient';
import { MUTATIONS } from '../../utils/constants';
import { RequestDetails } from '../RequestDetails/RequestDetails';
import { useWorkflow } from '../../workflow/hooks';
import { useLabelPolling } from './useLabelPolling';
import { getParams } from '../../utils/searchParams';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Page, PageWrap } from '../Page/Page';
import { Promo } from '../Promo/Promo';

// TODO: Replace with project wide date formatting
// TODO: Replace with actual dates
const date = new Date().toLocaleDateString('en-US', {
  month: 'short',
  day: '2-digit',
});

export const Complete = () => {
  const params = getParams();
  const isUnauthComplete = params?.complete === 'true';
  const navigate = useNavigate();
  const { retailer: retailerParam } = useParams({ strict: false });

  const submitResponse = queryClient.getQueryData<SubmitResponse>([
    MUTATIONS.SUBMIT,
  ]) as SubmitResponse;

  const submitInfo = submitResponse?.submit?.info?.[0];
  const cart = submitResponse?.submit?.cart;
  const email = submitResponse?.submit?.email;
  const label = submitInfo?.label;
  const deliveryMethod = cart?.delivery;
  const location = submitResponse?.submit?.cart?.location;

  const {
    order,
    orderNumber,
    config,
    retailer: workflowRetailer,
  } = useWorkflow();
  const retailer = retailerParam ?? workflowRetailer;
  const requestId = params?.request ?? submitResponse?.submit?.cart?.id;

  const [pollingEnabled, setPollingEnabled] = useState(
    !params?.shid && !isUnauthComplete,
  );

  const {
    shipmentData: { labelUrl, qrCodeUrl },
    status: pollingStatus,
  } = useLabelPolling({ requestId, enabled: pollingEnabled });

  useEffect(() => {
    if (pollingStatus === 'timeout') {
      setPollingEnabled(false);
    }
  }, [pollingStatus]);

  const derivedStatus: SubmitStatus = (() => {
    if (pollingStatus === 'success') {
      return 'REQUEST_COMPLETE';
    }
    if (submitResponse?.submit?.status) {
      return submitResponse.submit.status;
    }
    return 'REQUEST_RECEIVED';
  })();

  const replacementTokens = {
    order_number: orderNumber ?? submitInfo?.orderNumber ?? '',
    submitted_date: date,
    item_count: `${cart?.items.length}`,
    delivery_method_type: `${deliveryMethod?.type ?? ''}`,
    email: email ?? '',
  };

  const data = {
    email: email ?? undefined,
    requestId: requestId ?? undefined,
    label: { url: label?.url ?? labelUrl },
    qrCode: { url: qrCodeUrl },
    status: derivedStatus,
    deliveryMethod,
    deliveryType: deliveryMethod?.type,
    orderItems: order?.items ?? [],
    requestItems: cart?.items ?? [],
    location,
  };

  // Page includes authentication check; PageWrap does not
  const PageComponent = isUnauthComplete ? PageWrap : Page;

  const hasItemSelectionStep = config?.steps?.some(
    (step) => step.id === 'itemSelection',
  );

  const handleBack = () => {
    navigate({
      to: '/$retailer/item-selection',
      params: { retailer: retailer ?? '' },
      search: params ?? {},
      reloadDocument: true,
    });
  };

  return (
    <PageComponent>
      <Layout isFullWidth>
        <Promo id={'complete.pageTop'} />
        <RequestDetails
          headerTitle={{
            langKey: [
              `pdx.confirmation.${derivedStatus?.toLowerCase()}.${deliveryMethod?.type?.toLowerCase()}.${deliveryMethod?.packagingOption?.toLowerCase()}.header.title`,
              `pdx.confirmation.${derivedStatus?.toLowerCase()}.header.title`,
              `pdx.confirmation.header.title`,
            ],
            replacementTokens,
          }}
          headerSubtitle={{
            langKey: [
              `pdx.confirmation.${derivedStatus?.toLowerCase()}.${deliveryMethod?.type?.toLowerCase()}.${deliveryMethod?.packagingOption?.toLowerCase()}.header.subtitle`,
              `pdx.confirmation.${derivedStatus?.toLowerCase()}.header.subtitle`,
              `pdx.confirmation.header.subtitle`,
            ],
            replacementTokens,
          }}
          deliveryTitle={{
            langKey: [
              `pdx.confirmation.${derivedStatus?.toLowerCase()}.delivery.title.${[cart?.delivery?.type?.toLowerCase(), cart?.delivery?.packagingOption?.toLowerCase()].filter(Boolean).join('.')}`,
              `pdx.confirmation.delivery.title.${[cart?.delivery?.type?.toLowerCase(), cart?.delivery?.packagingOption?.toLowerCase()].filter(Boolean).join('.')}`,
            ],
            replacementTokens,
          }}
          data={data}
          replacementTokens={replacementTokens}
          pollingStatus={pollingStatus}
          onBack={hasItemSelectionStep ? handleBack : undefined}
        >
          <Promo id={'complete.pageBottom'} />
        </RequestDetails>
      </Layout>
    </PageComponent>
  );
};
